import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { VaultRequestError, type AgentManifest, type Vault as VaultHandle, type VaultSDK } from '@cef-ai/vault-sdk';
import { Alert, AgentAvatar, Button, TextField } from '@cere/cere-design-system';
import type { SettingDecl } from '@/lib/manifest/types';
import { connectCereWallet, type CereWallet } from '@/lib/clients/cere-wallet/connect';
import { signAndConnectAgent } from '@/lib/clients/vault/connect-agent';
import { claimVault, defaultVaultName } from '@/lib/clients/vault/claim';
import { buildSigningSdk } from '@/lib/clients/vault/signing-sdk';
import { mintVaultReadClient } from '@/lib/clients/vault/read-client';
import { toDisplayError } from '@/lib/vault/step-up-error';
import { decideConnectFollowup } from '@/lib/vault/connect-followup';
import { useConfig } from '@/src/config/context';
import { ConnectAgentForm } from './ConnectAgentForm';
import { ConnectStepRail } from './ConnectStepRail';

interface Props {
  agentId: string;
  version: string;
  manifestCid: string;
  agentServicePubkey: string;
  agentName: string;
  settings: SettingDecl[];
  iconUrl?: string;
  description?: string;
}

// The claim maps its progress kinds onto the UI's proxy banner. Two sources:
// the repo-owned onboarding switch (authorizing-gateway family) and
// vault-sdk 5.0.0's `ensure()` with its NEW `authorizing-vault` kind — the
// on-chain NonTransfer grant to vault-api, i.e. the consent step where the
// user's vault is allowed to store data on their behalf.
type ProxyPhase = 'initializing' | 'waiting' | 'authorizing' | null;

type Step =
  | 'idle' | 'wallet' | 'awaiting-proxy'
  | 'vault-name' | 'claiming' | 'connect' | 'connecting' | 'done';

const STEPS: { key: Step; label: string }[] = [
  { key: 'wallet', label: 'Connect Cere wallet' },
  { key: 'vault-name', label: 'Name your vault' },
  { key: 'connect', label: 'Connect agent' },
];

function progressToProxyPhase(kind: string): ProxyPhase {
  if (kind === 'gateway-authorization-required' || kind === 'authorizing-gateway') return 'initializing';
  if (kind === 'authorization-submitted' || kind === 'awaiting-chain-confirmation') return 'waiting';
  if (kind === 'authorizing-vault') return 'authorizing';
  return null;
}

export function VaultConnectFlow({
  agentId, version, manifestCid, agentServicePubkey, agentName, settings: settingDecls, iconUrl, description,
}: Props) {
  const navigate = useNavigate();
  const config = useConfig();
  const [step, setStep] = useState<Step>('idle');
  const [wallet, setWallet] = useState<CereWallet | null>(null);
  const [vaultName, setVaultName] = useState<string>('');
  const [vaultId, setVaultId] = useState<string | null>(null);
  const [vaultHandle, setVaultHandle] = useState<VaultHandle | null>(null);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [proxyPhase, setProxyPhase] = useState<ProxyPhase>(null);

  function setSetting(k: string, v: string) {
    setSettings((s) => ({ ...s, [k]: v }));
  }

  async function proceedAfterWallet(w: CereWallet, name: string, readSdk: VaultSDK) {
    try {
      await readSdk.vault.current(); // silent Bearer existence probe
    } catch (e) {
      if (!(e instanceof VaultRequestError) || e.status !== 404) {
        setError(toDisplayError(e));
        setStep('idle');
        return;
      }
      setVaultId(null);
      setStep('vault-name');
      return;
    }
    // Vault exists -> build a signing-capable handle for agents.connect.
    if (!config.s3Endpoint) { setError('Storage gateway endpoint unavailable — cannot open your vault.'); setStep('idle'); return; }
    try {
      const signingSdk = buildSigningSdk(w, {
        vaultBaseUrl: config.vaultBaseUrl,
        s3Endpoint: config.s3Endpoint,
        ...(config.garUrl ? { garUrl: config.garUrl } : {}),
        ...(config.rpcEndpoint ? { chainUrl: config.rpcEndpoint } : {}),
      });
      const existing = await signingSdk.vault.current();
      setVaultHandle(existing);
      setVaultId(existing.id);
      setVaultName(name);
      setStep('connect');
    } catch (e) {
      setError(toDisplayError(e));
      setStep('idle');
    }
  }

  async function startFlow() {
    setError(null);
    setProxyPhase(null);
    setStep('wallet');
    try {
      const w = await connectCereWallet(config.appEnv);
      setWallet(w);
      // Bearer, not direct-sign: `proceedAfterWallet` immediately probes
      // `readSdk.vault.current()`, and that read must not depend on the
      // wallet being keyed. See lib/clients/vault/read-client.ts.
      //
      // Deliberately the ONE-SHOT `mintVaultReadClient`, not the refreshing
      // variant the layout uses. This client lives only for the duration of
      // the claim flow and is superseded by `VaultLayout`'s (which does
      // refresh) the moment the claim completes — many times over inside the
      // 1h `VAULT_READ_TTL`. Arming a re-mint timer here would give this
      // short-lived component a teardown obligation it has no reason to own.
      const sdk = await mintVaultReadClient(w, {
        vaultBaseUrl: config.vaultBaseUrl,
        ...(config.garUrl ? { garUrl: config.garUrl } : {}),
      });
      const name = vaultName || defaultVaultName(w);
      setVaultName(name);
      await proceedAfterWallet(w, name, sdk);
    } catch (e) {
      setError(toDisplayError(e));
      setStep('idle');
      setProxyPhase(null);
    }
  }

  async function onClaimVault() {
    if (!wallet) return;
    if (!config.gatewayPublicKey) { setError('Gateway public key unavailable — vault claim cannot run onboarding.'); return; }
    if (!config.s3Endpoint) { setError('S3 gateway endpoint unavailable — vault claim cannot run onboarding.'); return; }
    setError(null);
    setStep('claiming');
    try {
      const claimed = await claimVault({
        wallet,
        vaultName,
        vaultBaseUrl: config.vaultBaseUrl,
        gatewayPublicKey: config.gatewayPublicKey,
        s3Endpoint: config.s3Endpoint,
        garUrl: config.garUrl,
        rpcEndpoint: config.rpcEndpoint,
        onProgress: (kind) => {
          const phase = progressToProxyPhase(kind);
          if (phase) setStep('awaiting-proxy');
          setProxyPhase(phase);
        },
      });
      setVaultHandle(claimed.handle);
      setVaultId(claimed.record.vaultId);
      setStep('connect');
    } catch (e) {
      setError(toDisplayError(e));
      setStep('vault-name');
    }
  }

  async function onConnectAgent() {
    if (!wallet || !vaultId) return;
    for (const s of settingDecls) {
      if (s.required && !settings[s.key]) { setError(`${s.label || s.key} is required`); return; }
    }
    setError(null);
    setStep('connecting');
    try {
      let vault = vaultHandle;
      if (!vault) {
        if (!config.s3Endpoint) throw new Error('Storage gateway endpoint unavailable — cannot open your vault.');
        const signingSdk = buildSigningSdk(wallet, {
          vaultBaseUrl: config.vaultBaseUrl,
          s3Endpoint: config.s3Endpoint,
          ...(config.garUrl ? { garUrl: config.garUrl } : {}),
          ...(config.rpcEndpoint ? { chainUrl: config.rpcEndpoint } : {}),
        });
        vault = await signingSdk.vault.current();
        setVaultHandle(vault);
      }
      const manifest: AgentManifest = {
        agentId,
        version,
        manifestCid,
        agentServicePubkey,
      };
      await signAndConnectAgent({
        vault,
        manifest,
        scope: 'default',
        settings,
      });
      setStep('done');
      navigate('/');
    } catch (e) {
      const followup = decideConnectFollowup(e);
      if (followup.kind === 'success') { setStep('done'); navigate('/'); return; }
      if (followup.kind === 'already-connected') {
        setStep('done');
        navigate(`/agents/${encodeURIComponent(agentId)}`);
        return;
      }
      setError(followup.message);
      setStep('connect');
    }
  }

  const currentStepIdx = step === 'idle' ? -1
    : (step === 'wallet' || step === 'awaiting-proxy') ? 0
    : (step === 'vault-name' || step === 'claiming') ? 1
    : (step === 'connect' || step === 'connecting') ? 2
    : 3;
  const showSteps = step !== 'idle' && step !== 'done';

  return (
    <div className="connect-shell">
      <section className="connect-card">
        <Link className="connect-back" to="/">← Back to Vault</Link>
        <div className="connect-head">
          <AgentAvatar seed={agentId} name={agentName} size={44} shadow {...(iconUrl ? { iconUrl } : {})} />
          <div className="connect-head__text">
            <div className="connect-overline">Connect agent</div>
            <h1 className="connect-title">{agentName}</h1>
            <div className="connect-head__meta"><span className="connect-mono-inline">v{version}</span></div>
          </div>
        </div>
        <p className="connect-sub">{description || 'Open your Vault, approve the connection, and install this Marketplace agent.'}</p>

        {step === 'idle' && (
          <Button fullWidth className="connect-action" onClick={startFlow}>
            Continue with Vault
          </Button>
        )}

        {showSteps && (
          <ConnectStepRail
            steps={STEPS.map((s) => ({ key: s.key, label: s.label }))}
            currentIndex={currentStepIdx}
          />
        )}

        {step === 'wallet' && <ConnectNotice>Initializing wallet…</ConnectNotice>}
        {step === 'awaiting-proxy' && proxyPhase === 'initializing' && <ConnectNotice>Initializing wallet…</ConnectNotice>}
        {step === 'awaiting-proxy' && proxyPhase === 'waiting' && <ConnectNotice>Securing wallet…</ConnectNotice>}
        {step === 'awaiting-proxy' && proxyPhase === 'authorizing' && <ConnectNotice>Allow your Vault to store data on your behalf — until you revoke</ConnectNotice>}

        {step === 'vault-name' && wallet && (
          <>
            <ConnectNotice severity="success" mono>wallet connected — {wallet.address.slice(0, 12)}…</ConnectNotice>
            <TextField
              fullWidth
              className="connect-field-control"
              label="Vault name"
              value={vaultName}
              onChange={(e) => setVaultName(e.target.value)}
            />
            <Button fullWidth className="connect-action" onClick={onClaimVault} disabled={!vaultName}>
              Claim vault
            </Button>
          </>
        )}

        {step === 'claiming' && <ConnectNotice>Creating security vault…</ConnectNotice>}
        {step === 'connect' && vaultId && (
          <ConnectAgentForm
            vaultId={vaultId}
            settingDecls={settingDecls}
            settings={settings}
            onChange={setSetting}
            onSubmit={onConnectAgent}
          />
        )}
        {step === 'connecting' && <ConnectNotice>Installing agent…</ConnectNotice>}
        {step === 'done' && vaultId && <ConnectNotice severity="success">Agent installed in vault <code>{vaultId}</code>. Opening your vault…</ConnectNotice>}
        {error && <ConnectNotice severity="error">{error}</ConnectNotice>}
      </section>
    </div>
  );
}

function ConnectNotice({
  children,
  severity = 'info',
  mono = false,
}: {
  children: ReactNode;
  severity?: 'success' | 'error' | 'warning' | 'info';
  mono?: boolean;
}) {
  return (
    <Alert
      className={`connect-notice${mono ? ' connect-mono' : ''}`}
      severity={severity}
      variant="standard"
    >
      {children}
    </Alert>
  );
}
